import React, { createContext, useContext, useReducer, useCallback, ReactNode } from 'react';
import { useDispatch } from 'react-redux';
import { RFQ, RFQEndUser, LoadingState, ErrorState, RFQContextType } from '../types';
import { setCurrentStep, setRFQ, refreshRFQ, refreshRFQData } from '../store/rfqSlice';
import apiService from '../services/api';
import { trpClient } from '../client/trpClient';
import { calculateWorkflowStep } from '../utils/workflowUtils';
import { sameRfqId } from '../utils/rfqIdentity';

// Action types
type RFQAction =
  | { type: 'SET_LOADING'; payload: LoadingState }
  | { type: 'SET_ERROR'; payload: ErrorState }
  | { type: 'SET_RFQS'; payload: RFQ[] }
  | { type: 'SET_CURRENT_RFQ'; payload: RFQ | null }
  | { type: 'ADD_RFQ'; payload: RFQ }
  | { type: 'UPDATE_RFQ'; payload: RFQ }
  | { type: 'REMOVE_RFQ'; payload: string }
  | { type: 'SET_CURRENT_STEP'; payload: string }
  | { type: 'CLEAR_ERROR' };

// State interface
interface RFQState {
  currentRFQ: RFQ | null;
  rfqs: RFQ[];
  loading: LoadingState;
  error: ErrorState;
}

// Initial state
const initialState: RFQState = {
  currentRFQ: null,
  rfqs: [],
  loading: { isLoading: false },
  error: { hasError: false },
};

// Reducer function
function rfqReducer(state: RFQState, action: RFQAction): RFQState {
  switch (action.type) {
    case 'SET_LOADING':
      return {
        ...state,
        loading: action.payload,
        error: { hasError: false }, // Clear error when loading starts
      };

    case 'SET_ERROR':
      return {
        ...state,
        loading: { isLoading: false },
        error: action.payload,
      };

    case 'CLEAR_ERROR':
      return {
        ...state,
        error: { hasError: false },
      };

    case 'SET_RFQS':
      return {
        ...state,
        rfqs: action.payload,
        loading: { isLoading: false },
        error: { hasError: false },
      };

    case 'SET_CURRENT_RFQ':
      return {
        ...state,
        currentRFQ: action.payload,
        loading: { isLoading: false },
        error: { hasError: false },
      };

    case 'SET_CURRENT_STEP':
      return {
        ...state,
        currentRFQ: state.currentRFQ
          ? { ...state.currentRFQ, workflow: { ...state.currentRFQ.workflow, currentStep: parseInt(action.payload) } }
          : null,
        loading: { isLoading: false },
        error: { hasError: false },
      };

    case 'ADD_RFQ':
      return {
        ...state,
        rfqs: [action.payload, ...state.rfqs],
        currentRFQ: action.payload,
        loading: { isLoading: false },
        error: { hasError: false },
      };

    case 'UPDATE_RFQ':
      const updatedRFQs = state.rfqs.map(rfq =>
        sameRfqId(rfq.rfqId, action.payload.rfqId) ? action.payload : rfq
      );
      return {
        ...state,
        rfqs: updatedRFQs,
        currentRFQ: sameRfqId(state.currentRFQ?.rfqId, action.payload.rfqId) ? action.payload : state.currentRFQ,
        loading: { isLoading: false },
        error: { hasError: false },
      };

    case 'REMOVE_RFQ':
      return {
        ...state,
        rfqs: state.rfqs.filter(rfq => !sameRfqId(rfq.rfqId, action.payload)),
        currentRFQ: sameRfqId(state.currentRFQ?.rfqId, action.payload) ? null : state.currentRFQ,
        loading: { isLoading: false },
        error: { hasError: false },
      };

    default:
      return state;
  }
}

// Create context
const RFQContext = createContext<RFQContextType | undefined>(undefined);

// Provider component
interface RFQProviderProps {
  children: ReactNode;
}

export function RFQProvider({ children }: RFQProviderProps) {
  const [state, dispatch] = useReducer(rfqReducer, initialState);
  const reduxDispatch = useDispatch();

  // Helper function to handle errors
  const handleError = useCallback((error: any, defaultMessage: string) => {
    const errorMessage = apiService.getErrorMessage(error);
    dispatch({
      type: 'SET_ERROR',
      payload: {
        hasError: true,
        message: errorMessage || defaultMessage,
        details: error.response?.data?.details,
      },
    });
  }, []);

  // Create new RFQ (optionally naming the technical end user at birth)
  const createRFQ = useCallback(async (name?: string, endUser?: RFQEndUser): Promise<RFQ> => {
    try {
      dispatch({ type: 'SET_LOADING', payload: { isLoading: true, message: 'Creating new RFQ...' } });

      const newRFQ = await trpClient.createRFQ(name, endUser);
      dispatch({ type: 'ADD_RFQ', payload: newRFQ });

      return newRFQ;
    } catch (error) {
      handleError(error, 'Failed to create RFQ');
      throw error;
    }
  }, [handleError]);

  // Fetch specific RFQ. preserveStep keeps the user's wizard position (used for
  // background refreshes triggered by chat tool calls). keepCriteria also keeps
  // the step-2 criteria on screen (refreshes that didn't change requirements).
  const fetchRFQ = useCallback(async (id: string, silent: boolean = false, preserveStep: boolean = false, keepCriteria: boolean = false) => {
    try {
      if (!silent) {
        dispatch({ type: 'SET_LOADING', payload: { isLoading: true, message: 'Loading RFQ...' } });
      }

      const rfq = await trpClient.getRFQ(id);

      // Set current RFQ in context
      dispatch({ type: 'SET_CURRENT_RFQ', payload: rfq });

      if (preserveStep && keepCriteria) {
        reduxDispatch(refreshRFQData(rfq));
      } else if (preserveStep) {
        reduxDispatch(refreshRFQ(rfq));
      } else {
        const { step } = calculateWorkflowStep(rfq);
        dispatch({ type: 'SET_CURRENT_STEP', payload: step.toString() });
        // Dump entire RFQ to Redux - let components decide what to show based on workflow step
        reduxDispatch(setRFQ(rfq));
        reduxDispatch(setCurrentStep(step));
      }
    } catch (error) {
      handleError(error, 'Failed to fetch RFQ');
    }
  }, [handleError, reduxDispatch]);

  // Update RFQ
  const updateRFQ = useCallback(async (rfqId: string, data: Partial<RFQ>) => {
    try {
      dispatch({ type: 'SET_LOADING', payload: { isLoading: true, message: 'Updating RFQ...' } });

      // For now, we'll update the local state directly
      // In a real implementation, this would make an API call
      const currentRFQ = state.currentRFQ;
      if (currentRFQ && sameRfqId(currentRFQ.rfqId, rfqId)) {
        const updatedRFQ = { ...currentRFQ, ...data };
        dispatch({ type: 'UPDATE_RFQ', payload: updatedRFQ });
      }
    } catch (error) {
      handleError(error, 'Failed to update RFQ');
    }
  }, [state.currentRFQ, handleError]);

  // Context value
  const contextValue: RFQContextType = {
    currentRFQ: state.currentRFQ,
    rfqs: state.rfqs,
    loading: state.loading,
    error: state.error,
    createRFQ,
    updateRFQ,
    fetchRFQ,
  };

  return (
    <RFQContext.Provider value={contextValue}>
      {children}
    </RFQContext.Provider>
  );
}

// Custom hook to use RFQ context
export function useRFQ(): RFQContextType {
  const context = useContext(RFQContext);
  if (context === undefined) {
    throw new Error('useRFQ must be used within a RFQProvider');
  }
  return context;
}

// Export context for testing
export { RFQContext };
